// Sidebar.jsx
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '../Components/UI/Icon';
import { useTranslation } from 'react-i18next';

export default function Sidebar({ navbarItems, isMobileOpen, onCloseMobile, modalOpen, onHover }) {
  const { t } = useTranslation();

  // Converti navbarItems nel formato atteso dalla sidebar
  const items = navbarItems ? navbarItems.map(item => ({
    label: item.label,
    icon: item.icon || 'circle', // default icon
    path: item.toLink,
    locked: item.locked || false
  })) : [];

  // Fallback alla lista statica se navbarItems non è fornito
  const fallbackItems = [
    { label: t('sidebar.dashboard'), icon: 'home', path: '/dashboard' },
    { label: t('pluginAdmin'), icon: 'listcheck', path: '/pluginadmin' },
    { label: t('compliance'), icon: 'starshield', path: '/compliance' },
    { label: t('cyberLog'), icon: 'newspaper', path: '/cyberlog' },
    { label: t('cyberSurveillance'), icon: 'bell', path: '/cybersurveillance' },
    { label: 'Networking Surveillance', icon: 'network', path: '/networking' },
    { label: t('myAsset'), icon: 'server', path: '/myassets' },
    { label: t('enterprise'), icon: 'enterprise', path: '/enterpriseConsent' },
  ];

  const sidebarItems = items.length > 0 ? items : fallbackItems;

  const getDesktopSidebar = () => (
    <div
      className={`
        fixed top-0 left-0 h-full z-40 bg-white border-r shadow-md
        ${modalOpen ? 'w-[70px] pointer-events-none opacity-60 select-none' : 'group hover:w-[240px] w-[70px] transition-all duration-300 ease-in-out'}
        flex flex-col overflow-hidden max-w-[240px]
      `}
      onMouseEnter={() => onHover && onHover(true)}
      onMouseLeave={() => onHover && onHover(false)}
    >
      <div className="h-16 px-4 flex items-center border-b text-lg text-gray-700" />

      <nav data-tour="sidebar-nav" className="flex-1 overflow-y-auto px-2 py-4 space-y-2">
        {sidebarItems.map((item) => (
          modalOpen ? (
            <div
              key={item.path}
              className="flex items-center gap-3 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap text-gray-400 opacity-60 select-none cursor-not-allowed"
            >
              <Icon name={item.icon} className="w-5 h-5 shrink-0" />
              <span className="truncate">{item.label}</span>
            </div>
          ) : (
            <NavLink
              key={item.path}
              to={item.locked ? '#' : item.path}
              onClick={item.locked ? (e) => e.preventDefault() : undefined}
              data-tour={`sidebar-${item.path.replace('/', '')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2 rounded-md text-sm font-medium transition whitespace-nowrap group ${
                  item.locked
                    ? 'text-gray-400 hover:bg-gray-50 cursor-not-allowed'
                    : isActive
                      ? 'bg-yellow-500 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-yellow-100'
                }`
              }
            >
              <Icon name={item.icon} className={`w-5 h-5 shrink-0 ${item.locked ? 'opacity-50' : ''}`} />
              <span className="hidden group-hover:inline truncate">{item.label}</span>
              {item.locked && (
                <svg className="w-3.5 h-3.5 shrink-0 hidden group-hover:inline text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              )}
            </NavLink>
          )
        ))}
      </nav>
    </div>
  );

  const getMobileSidebar = () => (
    <AnimatePresence>
      {isMobileOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCloseMobile}
          />
          <motion.div
            className={`fixed inset-y-0 left-0 z-50 bg-white w-[240px] max-w-full ${modalOpen ? 'pointer-events-none opacity-60 select-none' : ''}`}
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <div className="h-16 px-4 flex items-center justify-between border-b text-lg text-gray-700">
              <span className="font-bold">A-Sight</span>
              <button
                onClick={onCloseMobile}
                className="text-gray-600 text-2xl focus:outline-none"
                aria-label="Chiudi menu"
              >
                ×
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-2">
              {items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.locked ? '#' : item.path}
                  onClick={item.locked ? (e) => e.preventDefault() : onCloseMobile}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2 rounded-md text-sm font-medium transition group whitespace-nowrap ${
                      item.locked
                        ? 'text-gray-400 hover:bg-gray-50 cursor-not-allowed'
                        : isActive
                          ? 'bg-yellow-500 text-white shadow-sm'
                          : 'text-gray-700 hover:bg-yellow-100'
                    }`
                  }
                >
                  <Icon name={item.icon} className={`w-5 h-5 shrink-0 ${item.locked ? 'opacity-50' : ''}`} />
                  <span>{item.label}</span>
                  {item.locked && (
                    <svg className="w-3.5 h-3.5 shrink-0 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )}
                </NavLink>
              ))}
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <div className="lg:hidden">{getMobileSidebar()}</div>
      <div className="hidden lg:block">{getDesktopSidebar()}</div>
    </>
  );
}
